import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { isEqual } from 'lodash'

import { SecondaryButton, TransparentBgButton } from 'components/Shared/Button'
import { useTranslation } from 'react-i18next'
import {
  useCampaignSetup,
  useCampaignSetupDispatch,
} from 'components/Recommendations/Campaign/Context/CampaignSetupContext'
import {
  expandReelVariations,
  expandVariationId,
} from 'components/Recommendations/Campaign/Context/disabledVariations'
import LoadingProgressBar from 'components/Shared/LoadingProgressBar'
import PortalModal from 'components/Shared/PortalModal'
import PreviewAdsModal from './PreviewAdsModal'

// Real-data entry point for the redesigned "Preview Ads" modal — kept on the
// same button/location as v1's preview (`AdSuggestions`/`Previews/*`) since no
// design confirmation placed it elsewhere.
//
// `internalDisabledVariations` buffers in-modal toggles so nothing writes to
// the real setup until the user closes the modal, at which point a confirm
// prompt either syncs via `updateDisabledAdVariations` or discards back to
// the last-synced state.
const AdsSummaryItem = ({ title, value, loading, pageInfo }) => {
  const { t } = useTranslation()
  const [isModalOpen, setModalOpen] = useState(false)
  const { setup, disabledVariations } = useCampaignSetup()
  const { updateDisabledAdVariations } = useCampaignSetupDispatch()
  const [disabledVariationsPromptOpen, setDisabledVariationsPromptOpen] = useState(false)
  const [disabledVariationsLoading, setDisabledVariationsLoading] = useState(false)
  const [disabledVariationsError, setDisabledVariationsError] = useState(false)
  const [internalDisabledVariations, setInternalDisabledVariations] = useState(disabledVariations)

  useEffect(() => {
    if (
      !isModalOpen &&
      !disabledVariationsLoading &&
      !isEqual(disabledVariations, internalDisabledVariations)
    ) {
      setInternalDisabledVariations(disabledVariations)
    }
  }, [disabledVariations, isModalOpen, disabledVariationsLoading, internalDisabledVariations])

  useEffect(() => {
    if (!disabledVariationsLoading && !disabledVariationsError) {
      setDisabledVariationsPromptOpen(false)
    }
  }, [disabledVariationsLoading, disabledVariationsError])

  const syncDisabledVariations = () => {
    setDisabledVariationsLoading(true)
    setDisabledVariationsError(false)
    const expandedVariations = expandReelVariations(internalDisabledVariations, setup.creatives)
    updateDisabledAdVariations(expandedVariations, ({ success }) => {
      setDisabledVariationsLoading(false)
      if (success) {
        setInternalDisabledVariations(expandedVariations)
        setModalOpen(false)
      } else {
        setDisabledVariationsError(true)
      }
    })
  }

  const onModalClose = () => {
    if (!isEqual(disabledVariations, internalDisabledVariations)) {
      setDisabledVariationsPromptOpen(true)
    } else {
      setModalOpen(false)
    }
  }

  const toggleVariation = (ad, tc) => {
    const variationIds = expandVariationId(ad.variationId, setup.creatives, tc)
    const tcDisabledVariations = [...(internalDisabledVariations[tc] || [])]

    for (const variationId of variationIds) {
      const existingIndex = tcDisabledVariations.indexOf(variationId)
      if (existingIndex > -1) {
        tcDisabledVariations.splice(existingIndex, 1)
      } else {
        tcDisabledVariations.push(variationId)
      }
    }

    setInternalDisabledVariations({
      ...internalDisabledVariations,
      [tc]: tcDisabledVariations,
    })
  }

  return (
    <div className="card card-no-padding-mobile rounded flex-row">
      <div className="d-flex w-100">
        <div>
          <div className="summary_item_icon">
            <i className="icon-campaign" />
          </div>
        </div>
        <div className="d-flex justify-content-between flex-wrap gap-3 w-100">
          <p className="summary_item_content">
            {loading ? (
              <LoadingProgressBar visible className="ads-preview-loader" />
            ) : (
              <strong>{value}</strong>
            )}
            <span>{title}</span>
          </p>
          <div className="summary_item_button">
            <SecondaryButton
              text={t('Recommendations.summary.previewAds')}
              color="navy"
              size="small"
              onClick={() => setModalOpen(true)}
              disabled={loading}
            />
          </div>
        </div>
      </div>

      <PreviewAdsModal
        isOpen={isModalOpen && !disabledVariationsPromptOpen}
        onClose={onModalClose}
        creatives={setup.creatives}
        setup={setup}
        pageInfo={pageInfo}
        disabledVariations={internalDisabledVariations}
        onToggleVariation={toggleVariation}
      />

      <PortalModal
        isOpen={disabledVariationsPromptOpen}
        onClickOutside={() => setDisabledVariationsPromptOpen(false)}
      >
        <div
          className="content compact text-center"
          style={{ maxWidth: '420px', minHeight: '178px' }}
        >
          {!disabledVariationsLoading && !disabledVariationsError ? (
            <>
              <h1 className="autofill_warning-header">
                {t('Recommendations.summary.confirmChanges')}
              </h1>
              <p className="autofill_warning-msg">
                {t('Recommendations.summary.confirmChangesDescription')}
              </p>
              <div className="footer">
                <TransparentBgButton
                  text={t('common.cancel')}
                  onClick={() => setDisabledVariationsPromptOpen(false)}
                  color="navy"
                />
                <SecondaryButton
                  color="orange"
                  text={t('common.confirm')}
                  onClick={syncDisabledVariations}
                  type="button"
                  disabledTitle=""
                />
              </div>
            </>
          ) : disabledVariationsLoading ? (
            <LoadingProgressBar visible className="autofill_falling-dots" />
          ) : (
            <>
              <div className="text-center mt-3">
                <h1 className="autofill_warning-header">{t('common.whoops')}!</h1>
                <p className="autofill_warning-msg mb-4">{t('common.requestError')}</p>
              </div>
              <SecondaryButton
                color="navy"
                disabled={disabledVariationsLoading}
                onClick={syncDisabledVariations}
                type="button"
                text={t('common.tryAgain')}
              />
            </>
          )}
        </div>
      </PortalModal>
    </div>
  )
}

AdsSummaryItem.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  pageInfo: PropTypes.shape({
    page_name: PropTypes.string,
    profile_picture_url: PropTypes.string,
  }),
}

export default AdsSummaryItem
